import { Reveal } from "./Reveal";

const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.4, strokeLinecap: "round", strokeLinejoin: "round" };

function IconBolt() {
    return (
        <svg viewBox="0 0 32 32" className="h-9 w-9" {...stroke} aria-hidden="true">
            <path d="M17.6 4.5 9.2 17.4h5L12.9 27.5 21.8 14.6h-5z" />
        </svg>
    );
}
function IconCompress() {
    return (
        <svg viewBox="0 0 32 32" className="h-9 w-9" {...stroke} aria-hidden="true">
            <path d="M3 10l8 6-8 6V10z" />
            <path d="M29 10l-8 6 8 6V10z" />
            <path d="M16 4v24" strokeDasharray="2 4" />
        </svg>
    );
}
function IconCavern() {
    return (
        <svg viewBox="0 0 32 32" className="h-9 w-9" {...stroke} aria-hidden="true">
            <path d="M2 10h28" />
            <path d="M6 10c0 8 4.5 14 10 14s10-6 10-14" />
            <path d="M12 20h8" strokeDasharray="2 3" />
        </svg>
    );
}
function IconExpander() {
    return (
        <svg viewBox="0 0 32 32" className="h-9 w-9" {...stroke} aria-hidden="true">
            <circle cx="16" cy="16" r="5" />
            <path d="M16 3v5M16 24v5M3 16h5M24 16h5M6.8 6.8l3.5 3.5M21.7 21.7l3.5 3.5M25.2 6.8l-3.5 3.5M10.3 21.7l-3.5 3.5" />
        </svg>
    );
}
function IconPylon() {
    return (
        <svg viewBox="0 0 32 32" className="h-9 w-9" {...stroke} aria-hidden="true">
            <path d="M11 3h10M13 3l-3 26M19 3l3 26M6.5 11h19M9 20h14M12 3l8 17M20 3l-8 17" />
        </svg>
    );
}

export function CaesDiagram({ dark = true }) {
    const steps = [
        { icon: <IconBolt />, label: "Renewable electricity" },
        { icon: <IconCompress />, label: "Compression" },
        { icon: <IconCavern />, label: "Compressed air storage" },
        { icon: <IconExpander />, label: "Expansion / generation" },
        { icon: <IconPylon />, label: "Power grid" },
    ];
    const line = dark ? "text-blue-bright" : "text-blue";
    const label = dark ? "text-white/70" : "text-ink-2";
    const rule = dark ? "border-blue-bright/30" : "border-blue/30";
    return (
        <div data-testid="caes-diagram">
            <div className="grid gap-8 md:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr] md:items-start md:gap-3">
                {steps.map((s, i) => (
                    <div key={s.label} className="contents">
                        <Reveal delay={i * 0.1} className="text-center">
                            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-full border ${dark ? "border-blue-bright/40 bg-navy-2" : "border-blue/30 bg-white"} ${line}`}>
                                {s.icon}
                            </div>
                            <p className={`mt-4 font-mono text-[10px] uppercase tracking-[0.18em] sm:text-[11px] ${label}`}>
                                <span className={`mr-2 ${dark ? "text-blue-bright" : "text-blue"}`}>0{i + 1}</span>
                                {s.label}
                            </p>
                        </Reveal>
                        {i < steps.length - 1 && (
                            <div className="hidden items-center justify-center md:flex md:pt-8">
                                <svg className="h-2 w-12 text-blue-bright lg:w-16" aria-hidden="true">
                                    <line x1="0" y1="4" x2="64" y2="4" stroke="currentColor" strokeWidth="1" strokeDasharray="4 5" className="flow-line" />
                                </svg>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function MiniSchematic({ variant }) {
    const adiabatic = variant === "adiabatic";
    return (
        <svg viewBox="0 0 560 250" className="w-full" role="img" aria-label={`${adiabatic ? "Adiabatic" : "Diabatic"} CAES schematic`}>
            <g stroke="currentColor" strokeWidth="1.3" fill="none" strokeLinecap="round">
                <rect x="20" y="100" width="86" height="52" />
                <text x="63" y="130" textAnchor="middle" fontSize="10" fill="currentColor" stroke="none" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">GRID</text>
                <path d="M106 126h44" strokeDasharray="4 5" className="flow-line" />
                <path d="M143 121l7 5-7 5" />
                <rect x="150" y="100" width="96" height="52" />
                <text x="198" y="130" textAnchor="middle" fontSize="10" fill="currentColor" stroke="none" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">COMPRESS</text>
                <path d="M246 126h48" strokeDasharray="4 5" className="flow-line" />
                <path d="M287 121l7 5-7 5" />
                <path d="M300 96c0 20 9 32 30 32s30-12 30-32z" transform="translate(0 -14)" />
                <text x="330" y="118" textAnchor="middle" fontSize="10" fill="currentColor" stroke="none" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">AIR STORE</text>
                <path d="M366 126h44" strokeDasharray="4 5" className="flow-line" />
                <path d="M403 121l7 5-7 5" />
                <rect x="420" y="100" width="96" height="52" />
                <text x="468" y="130" textAnchor="middle" fontSize="10" fill="currentColor" stroke="none" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">EXPAND</text>
                {adiabatic ? (
                    <>
                        <rect x="238" y="8" width="130" height="38" strokeDasharray="0" />
                        <text x="303" y="31" textAnchor="middle" fontSize="10" fill="currentColor" stroke="none" fontFamily="IBM Plex Mono, monospace" letterSpacing="1">THERMAL STORE</text>
                        <path d="M198 100v-38M198 62h32" />
                        <path d="M226 57l7 5-7 5" />
                        <path d="M368 62h60v38" />
                        <path d="M423 96l5 7 5-7" transform="translate(2 -3)" />
                        <text x="330" y="70" textAnchor="middle" fontSize="9" fill="currentColor" stroke="none" opacity="0.7" fontFamily="IBM Plex Mono, monospace">HEAT RECYCLED</text>
                    </>
                ) : (
                    <>
                        <path d="M198 100V64" />
                        <path d="M193 72l5-9 5 9" />
                        <text x="198" y="54" textAnchor="middle" fontSize="9" fill="currentColor" stroke="none" opacity="0.7" fontFamily="IBM Plex Mono, monospace">HEAT RELEASED</text>
                        <path d="M468 152v46H300v-24" />
                        <path d="M295 182l5 9 5-9" transform="translate(0 -12)" />
                        <text x="390" y="192" textAnchor="middle" fontSize="9" fill="currentColor" stroke="none" opacity="0.7" fontFamily="IBM Plex Mono, monospace">REHEAT BEFORE EXPANSION</text>
                    </>
                )}
            </g>
        </svg>
    );
}

export function DiabaticAdiabatic() {
    const panels = [
        {
            variant: "diabatic",
            title: "Diabatic CAES",
            body: "Compression heat is released. Before expansion, the stored air is reheated — typically with supplementary fuel — to maximise power output.",
        },
        {
            variant: "adiabatic",
            title: "Adiabatic CAES",
            body: "Compression heat is captured in a thermal store and returned to the air before expansion — recovering energy without supplementary fuel.",
        },
    ];
    return (
        <div className="grid gap-px overflow-hidden border border-line bg-line lg:grid-cols-2" data-testid="caes-variants">
            {panels.map((p) => (
                <div key={p.variant} className="bg-white p-8 sm:p-10">
                    <div className="flex items-baseline justify-between gap-4">
                        <h3 className="h3-compact">{p.title}</h3>
                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-3">{p.variant}</span>
                    </div>
                    <div className="mt-8 text-navy">
                        <MiniSchematic variant={p.variant} />
                    </div>
                    <p className="body-sm mt-6 max-w-md">{p.body}</p>
                </div>
            ))}
        </div>
    );
}
